import { useCallback } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { addItemToCache } from '@ts-core/core/utils/apolloUtils';

import { getAcademyProfessorsQuery, GetAcademyProfessorsQueryResult } from '../graphql/queries/getAcademyProfessorsQuery';
import { getAcademyRolesQuery, GetAcademyRolesQueryResult } from '../graphql/queries/getAcademyRolesQuery';
import {
  toggleProfessorMutation,
  ToggleProfessorMutationResult,
  ToggleProfessorMutationVariables,
} from '../graphql/mutations/toggleProfessorMutation';
import {
  setAcademyProfessorRoleMutation,
  SetAcademyProfessorRoleMutationResult,
  SetAcademyProfessorRoleMutationVariables,
} from '../graphql/mutations/setAcademyProfessorRoleMutation';
import {
  addAcademyProfessorMutation,
  AddAcademyProfessorMutationResult,
  AddAcademyProfessorMutationVariables,
} from '../graphql/mutations/addAcademyProfessorMutation';

import { AcademyProfessor, AcademyRole } from '../types';
import { AddAcademyProfessorFormInput } from '../types/form';

interface AcademyProfessorsHookOutput {
  professors?: AcademyProfessor[];
  loadingProfessors: boolean;

  roles?: AcademyRole[];
  loadingRoles: boolean;

  onToggleProfessor: (professor: AcademyProfessor) => void;
  onSetProfessorRole: (professor: AcademyProfessor, role: AcademyRole) => void;

  addAcademyProfessorLoading: boolean;
  addAcademyProfessorError?: string;
  onAddAcademyProfessor: (input: AddAcademyProfessorFormInput, onSuccess: () => void) => void;
}

export default function useAcademyProfessors(): AcademyProfessorsHookOutput {
  const { showErrorNotification } = useSnackbarNotifications();

  const { data: academyProfessors, loading: loadingProfessors } = useQuery<GetAcademyProfessorsQueryResult>(getAcademyProfessorsQuery);

  const { data: academyRoles, loading: loadingRoles } = useQuery<GetAcademyRolesQueryResult>(getAcademyRolesQuery);

  const [toggleProfessor] = useMutation<ToggleProfessorMutationResult, ToggleProfessorMutationVariables>(toggleProfessorMutation, {
    onError: showErrorNotification,
  });
  const onToggleProfessor = useCallback(
    (professor: AcademyProfessor) => {
      const updatedProfessor = {
        ...professor,
        active: !professor.active,
      };

      toggleProfessor({
        variables: {
          professorId: professor.id,
          action: updatedProfessor.active ? 'enable' : 'disable',
        },
        optimisticResponse: {
          academyProfessor: updatedProfessor,
        },
      });
    },
    [toggleProfessor]
  );

  const [setAcademyProfessorRole] = useMutation<SetAcademyProfessorRoleMutationResult, SetAcademyProfessorRoleMutationVariables>(
    setAcademyProfessorRoleMutation,
    {
      onError: showErrorNotification,
    }
  );
  const onSetProfessorRole = useCallback(
    (professor: AcademyProfessor, role: AcademyRole) => {
      setAcademyProfessorRole({
        variables: {
          professorId: professor.id,
          input: {
            role: role.id,
          },
        },
        optimisticResponse: {
          academyProfessor: {
            ...professor,
            role,
          },
        },
      });
    },
    [setAcademyProfessorRole]
  );

  const [addAcademyProfessor, { loading: addAcademyProfessorLoading, error: addAcademyProfessorError }] = useMutation<
    AddAcademyProfessorMutationResult,
    AddAcademyProfessorMutationVariables
  >(addAcademyProfessorMutation, {
    update: addItemToCache<AddAcademyProfessorMutationResult, GetAcademyProfessorsQueryResult>({
      query: getAcademyProfessorsQuery,
      queryName: 'academyProfessors',
      mutationName: 'academyProfessor',
    }),
  });
  const onAddAcademyProfessor = useCallback(
    (input: AddAcademyProfessorFormInput, onSuccess: () => void) => {
      addAcademyProfessor({ variables: { input } }).then(onSuccess);
    },
    [addAcademyProfessor]
  );

  return {
    loadingProfessors,
    professors: academyProfessors?.academyProfessors,

    loadingRoles,
    roles: academyRoles?.roles,

    onToggleProfessor,

    onSetProfessorRole,

    addAcademyProfessorLoading,
    addAcademyProfessorError: getErrorMessage({ error: addAcademyProfessorError }),
    onAddAcademyProfessor,
  };
}
